import { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Divider,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { ErrorAlert } from '../../components/common/ErrorAlert';
import { LoadingState } from '../../components/common/LoadingState';
import { EngagementPeopleDrawer } from '../../components/common/EngagementPeopleDrawer';
import {
  defaultAnalyticsRange,
  useAnalyticsArticle,
  useAnalyticsArticles,
  useAnalyticsAudience,
  useAnalyticsOps,
  useAnalyticsOverview,
} from '../../hooks/useAnalytics';
import { SHOW_NEWS_SOURCES } from '../../config/app';
import { alpha, shellSx, tokens } from '../../theme/tokens';
import { articleStatusLabel, displayTitle, roleLabel } from '../../utils/labels';

const SECTIONS = [
  { id: 'overview', label: 'Overview' },
  { id: 'posts', label: 'Posts' },
  { id: 'audience', label: 'Audience' },
  { id: 'ops', label: 'Ops' },
];

const PRESETS = [
  { days: 7, label: '7d' },
  { days: 30, label: '30d' },
  { days: 90, label: '90d' },
];

function MetricCard({ label, value, onClick }) {
  const body = (
    <CardContent sx={{ py: 1.5, px: 1.75, '&:last-child': { pb: 1.5 } }}>
      <Typography
        variant="h4"
        sx={{
          fontFamily: '"Fraunces", Georgia, serif',
          fontSize: { xs: '1.25rem', sm: '1.6rem' },
        }}
      >
        {value}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
        {label}
      </Typography>
    </CardContent>
  );
  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      {onClick ? (
        <CardActionArea onClick={onClick} sx={{ height: '100%' }}>
          {body}
        </CardActionArea>
      ) : (
        body
      )}
    </Card>
  );
}

function DayBars({ series, valueKey = 'views' }) {
  const max = Math.max(1, ...series.map((d) => d[valueKey] || 0));
  return (
    <Stack direction="row" alignItems="flex-end" spacing={0.5} sx={{ height: 88, mt: 1 }}>
      {series.map((d) => (
        <Box
          key={d.day}
          title={`${d.day}: ${d[valueKey] || 0}`}
          sx={{
            flex: 1,
            minWidth: 4,
            height: `${Math.max(4, ((d[valueKey] || 0) / max) * 100)}%`,
            bgcolor: tokens.brandFill,
            borderRadius: '3px 3px 0 0',
          }}
        />
      ))}
    </Stack>
  );
}

function SectionChip({ label, selected, onClick }) {
  return (
    <Chip
      label={label}
      onClick={onClick}
      variant={selected ? 'filled' : 'outlined'}
      color={selected ? 'secondary' : 'default'}
      sx={
        selected
          ? {
              bgcolor: tokens.brandFill,
              color: '#fff',
              '& .MuiChip-label': { color: '#fff' },
              '&:hover': { bgcolor: tokens.brandInk },
            }
          : {
              bgcolor: 'transparent',
              color: tokens.onShell,
              borderColor: alpha(tokens.onShell, 0.45),
              '& .MuiChip-label': { color: tokens.onShell },
              '&:hover': {
                borderColor: tokens.brand,
                bgcolor: alpha('#FFFFFF', 0.1),
              },
            }
      }
    />
  );
}

function RangeToolbar({ range, onPreset }) {
  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      flexWrap="wrap"
      useFlexGap
      sx={{ mb: 2 }}
    >
      <Typography variant="body2" sx={shellSx.textMuted}>
        Range
      </Typography>
      {PRESETS.map((p) => (
        <SectionChip
          key={p.days}
          label={p.label}
          selected={range.preset === p.days}
          onClick={() => onPreset(p.days)}
        />
      ))}
    </Stack>
  );
}

function OverviewSection({ range, onOpenPost }) {
  const { data, loading, error } = useAnalyticsOverview(range);
  if (error) return <ErrorAlert message={error} />;
  if (loading || !data) return <LoadingState label="Loading overview…" />;
  const k = data.kpis || {};

  const metrics = [
    { label: 'Published (range)', value: k.publishedInRange ?? 0 },
    { label: 'Total views', value: k.views ?? 0 },
    { label: 'Unique reach', value: k.uniqueReach ?? 0 },
    { label: 'Likes', value: k.likes ?? 0 },
    { label: 'Shares', value: k.shares ?? 0 },
    { label: 'Comments (range)', value: k.commentsInRange ?? 0 },
    { label: 'Active engagers', value: k.activeEngagers ?? 0 },
  ];

  return (
    <Box>
      <Grid container spacing={1.25} sx={{ mb: 2 }}>
        {metrics.map((m) => (
          <Grid item xs={6} sm={4} md={3} key={m.label}>
            <MetricCard label={m.label} value={m.value} onClick={m.onClick} />
          </Grid>
        ))}
      </Grid>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 0.5 }}>
            Daily views
          </Typography>
          <DayBars series={data.daily || []} valueKey="views" />
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Top posts
          </Typography>
          <Stack spacing={1}>
            {(data.topPosts || []).map((p) => (
              <Button
                key={p.id}
                onClick={() => onOpenPost(p.id)}
                sx={{ justifyContent: 'space-between', textTransform: 'none' }}
              >
                <Typography noWrap sx={{ flex: 1, textAlign: 'left' }}>
                  {displayTitle(p.title)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {p.viewsInRange} views
                </Typography>
              </Button>
            ))}
            {(data.topPosts || []).length === 0 && (
              <Typography color="text.secondary">No view events yet.</Typography>
            )}
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}

function PostsSection({ range, selectedId, onSelect }) {
  const { data, loading, error } = useAnalyticsArticles(range, 'views');
  const detail = useAnalyticsArticle(selectedId, range);
  const [peopleDrawer, setPeopleDrawer] = useState(null);

  if (error) return <ErrorAlert message={error} />;
  if (loading || !data) return <LoadingState label="Loading posts…" />;

  return (
    <Box>
      <Box sx={{ overflowX: 'auto', mb: 2 }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Title</TableCell>
                <TableCell align="right">Views in range</TableCell>
                <TableCell align="right">Reach</TableCell>
                <TableCell align="right">Likes</TableCell>
                <TableCell align="right">Shares</TableCell>
                <TableCell align="right">Comments</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(data.items || []).slice(0, 50).map((row) => (
                <TableRow
                  key={row.id}
                  hover
                  selected={selectedId === row.id}
                  onClick={() => onSelect(row.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onSelect(row.id);
                    }
                  }}
                  tabIndex={0}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 280 }}>
                      {displayTitle(row.title)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">{row.viewsInRange ?? row.views}</TableCell>
                  <TableCell align="right">{row.uniqueReach}</TableCell>
                  <TableCell align="right">{row.likes}</TableCell>
                  <TableCell align="right">{row.shares}</TableCell>
                  <TableCell align="right">{row.comments}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {selectedId && (
        <Card>
          <CardContent>
            {detail.loading && <LoadingState label="Loading post…" />}
            {detail.error && <ErrorAlert message={detail.error} />}
            {detail.data && (
              <Box>
                <Typography variant="h6">{displayTitle(detail.data.article.title)}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  {articleStatusLabel(detail.data.article.status)} · views {detail.data.article.viewCount} ·
                  reach {detail.data.article.uniqueReach} · shares{' '}
                  {detail.data.sharedByCount ?? detail.data.article.shareCount ?? 0}
                </Typography>
                <DayBars series={detail.data.dailyViews || []} valueKey="views" />
                <Divider sx={{ my: 2 }} />
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setPeopleDrawer('views')}
                  >
                    Viewed by {(detail.data.article.uniqueReach ?? 0).toLocaleString()}
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setPeopleDrawer('likes')}
                  >
                    Liked by {(detail.data.likedCount ?? 0).toLocaleString()}
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setPeopleDrawer('comments')}
                  >
                    Commented by {(detail.data.funnel?.comments ?? detail.data.comments?.length ?? 0).toLocaleString()}
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setPeopleDrawer('shares')}
                  >
                    Shared by {(detail.data.sharedByCount ?? 0).toLocaleString()}
                  </Button>
                </Stack>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Recent comments
                </Typography>
                <Stack spacing={1}>
                  {(detail.data.comments || []).slice(0, 8).map((c) => (
                    <Box key={c.id}>
                      <Typography variant="body2" fontWeight={600}>
                        {c.userName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {c.text}
                      </Typography>
                    </Box>
                  ))}
                  {(detail.data.comments || []).length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                      No comments
                    </Typography>
                  )}
                </Stack>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      <EngagementPeopleDrawer
        open={Boolean(peopleDrawer)}
        onClose={() => setPeopleDrawer(null)}
        articleId={selectedId}
        kind={peopleDrawer || 'likes'}
      />
    </Box>
  );
}

function AudienceSection({ range }) {
  const { data, loading, error } = useAnalyticsAudience(range);
  if (error) return <ErrorAlert message={error} />;
  if (loading || !data) return <LoadingState label="Loading audience…" />;

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Top engagers
            </Typography>
            <Stack spacing={1}>
              {(data.topEngagers || []).map((u) => (
                <Box key={u.id} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">
                    {u.name}
                    {u.role &&
                    roleLabel(u.role) &&
                    roleLabel(u.role).toLowerCase() !== String(u.name || '').toLowerCase()
                      ? ` · ${roleLabel(u.role)}`
                      : ''}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {u.events} events
                  </Typography>
                </Box>
              ))}
              {(data.topEngagers || []).length === 0 && (
                <Typography color="text.secondary">No engagement events yet.</Typography>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} md={6}>
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Devices
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Total {data.deviceTotal ?? 0}
            </Typography>
            {Object.entries(data.devicesByPlatform || {}).map(([platform, count]) => (
              <Typography key={platform} variant="body2">
                {platform}: {count}
              </Typography>
            ))}
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Users by role
            </Typography>
            {Object.entries(data.usersByRole || {}).map(([role, count]) => (
              <Typography key={role} variant="body2">
                {roleLabel(role) || role}: {count}
              </Typography>
            ))}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
}

function OpsSection({ range }) {
  const { data, loading, error } = useAnalyticsOps(range);
  if (error) return <ErrorAlert message={error} />;
  if (loading || !data) return <LoadingState label="Loading ops…" />;
  const w = data.workflow || {};

  return (
    <Box>
      <Grid container spacing={1.25} sx={{ mb: 2 }}>
        {[
          [articleStatusLabel('draft'), w.draft],
          [articleStatusLabel('pending'), w.pending],
          [articleStatusLabel('approved'), w.approved],
          [articleStatusLabel('published'), w.published],
          [articleStatusLabel('rejected'), w.rejected],
          ['Published in range', w.publishedInRange],
        ].map(([label, value]) => (
          <Grid item xs={6} sm={4} md={2} key={label}>
            <MetricCard label={label} value={value ?? 0} />
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} md={SHOW_NEWS_SOURCES ? 5 : 12}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Top publishers
              </Typography>
              {(data.topPublishers || []).map((p) => (
                <Box
                  key={p.id}
                  sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}
                >
                  <Typography variant="body2">{p.name}</Typography>
                  <Typography variant="caption">{p.count}</Typography>
                </Box>
              ))}
              {(data.topPublishers || []).length === 0 && (
                <Typography color="text.secondary">None in range.</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
        {SHOW_NEWS_SOURCES ? (
          <Grid item xs={12} md={7}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Sources
                </Typography>
                {(data.sources || []).map((s) => (
                  <Box
                    key={s.id}
                    sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}
                  >
                    <Typography variant="body2">
                      {s.name}
                      {!s.active ? ' (paused)' : ''}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {s.articleCount} articles
                    </Typography>
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Grid>
        ) : null}
      </Grid>
    </Box>
  );
}

export function AnalyticsTab() {
  const [range, setRange] = useState(() => defaultAnalyticsRange(30));
  const [section, setSection] = useState('overview');
  const [selectedPostId, setSelectedPostId] = useState(null);

  const rangeLabel = useMemo(() => {
    try {
      return `${new Date(range.from).toLocaleDateString()} – ${new Date(
        range.to,
      ).toLocaleDateString()}`;
    } catch {
      return '';
    }
  }, [range.from, range.to]);

  return (
    <Box>
      <Typography sx={{ ...shellSx.textMuted, mb: 1.5 }}>
        Likes, comments, and views for published stories · {rangeLabel}
      </Typography>

      <RangeToolbar
        range={range}
        onPreset={(days) => setRange(defaultAnalyticsRange(days))}
      />

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2.5 }}>
        {SECTIONS.map((s) => (
          <SectionChip
            key={s.id}
            label={s.label}
            selected={section === s.id}
            onClick={() => setSection(s.id)}
          />
        ))}
      </Stack>

      {section === 'overview' && (
        <OverviewSection
          range={range}
          onOpenPost={(id) => {
            setSelectedPostId(id);
            setSection('posts');
          }}
        />
      )}
      {section === 'posts' && (
        <PostsSection
          range={range}
          selectedId={selectedPostId}
          onSelect={setSelectedPostId}
        />
      )}
      {section === 'audience' && <AudienceSection range={range} />}
      {section === 'ops' && <OpsSection range={range} />}
    </Box>
  );
}
